import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

import { relativeTime } from "../../../../lib/relativeTime.js";

/**
 * The ticket detail page's layout + time primitives, extracted from
 * `TicketDetailPage.tsx`: the section card, the right-rail definition row,
 * the em-dash empty marker, and the relative/absolute timestamp cell.
 */

/** Reusable section card — title bar + optional count badge + body. */
export function Section({
  title,
  count,
  tight,
  children,
}: {
  title: string;
  count?: number;
  tight?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      component="section"
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (th) => th.shadows[1],
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 1, borderColor: "divider", px: 2, py: 1 }}>
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
          {title}
        </Typography>
        {typeof count === "number" && count > 0 && (
          <Box
            component="span"
            sx={{ borderRadius: 999, bgcolor: "action.hover", px: 1, py: 0.25, fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
          >
            {count}
          </Box>
        )}
      </Box>
      <Box sx={{ p: tight ? 1 : 2 }}>{children}</Box>
    </Box>
  );
}

export function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "100px 1fr",
        alignItems: "center",
        gap: 1.5,
        borderRadius: 1,
        px: 1,
        py: 1,
        transition: "background-color 150ms",
        // Subtle row dividers — quieter than full borders, brings the
        // panel closer to a definition-list / Notion-style sidebar.
        "& + &": { borderTop: 1, borderColor: (t) => alpha(t.palette.divider, 0.6) },
        "&:hover": { bgcolor: "surfaceSoft" },
      }}
    >
      <Box component="dt" sx={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box component="dd" sx={{ minWidth: 0, m: 0, fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

export function Empty(): JSX.Element {
  return <Box component="span" sx={{ fontSize: 14, color: "text.disabled" }}>—</Box>;
}

/** Time cell — absolute on hover via `title`, relative as the primary label. */
export function TimeStamp({ iso }: { iso: string }): JSX.Element {
  const date = new Date(iso);
  return (
    <Box
      component="span"
      title={date.toLocaleString()}
      sx={{ fontSize: 14, color: "text.primary" }}
    >
      {relativeTime(date)}
    </Box>
  );
}
